// src/services/walletConnectService.js
import { Core } from '@walletconnect/core';
import { WalletKit } from '@reown/walletkit';
import { buildApprovedNamespaces, getSdkError } from '@walletconnect/utils';

// Storage key for WalletConnect sessions
const SESSIONS_STORAGE_KEY = 'bitdog_walletconnect_sessions';

// Create a singleton instance of the service
class WalletConnectService {
  constructor() {
    this.core = null;
    this.walletKit = null;
    this.initialized = false;
    this.pendingProposal = null;
    this.activeSessions = [];
    this.pendingRequests = [];
    this.onSessionProposalCallback = null;
    this.onSessionRequestCallback = null;
    this.onAuthRequestCallback = null;
    
    // Track where to handle WalletConnect requests
    this.wcHandlerLocation = null;
    
    // Session origin tracking to know where each session should be handled
    this.sessionOrigins = new Map();

    // Add retry mechanism for failed connections
    this.connectionRetries = 0;
    this.maxRetries = 5; // Increased from 3 to 5
    this.reconnectTimeout = null;
    
    // Queue for operations that need to wait for initialization
    this.operationQueue = [];
    
    // Flag to track if initialization is in progress
    this.initializationInProgress = false;
    
    // Tracking for signature requests
    this.lastSignatureRequestMethod = null;
    this.lastPermitContext = null;
  }

  // Set the WalletConnect handler location
  setWcHandlerLocation(location) {
    console.log(`Setting WalletConnect handler location to: ${location}`);
    this.wcHandlerLocation = location;
  }

  // Get the current WalletConnect handler location
  getWcHandlerLocation() {
    return this.wcHandlerLocation;
  }
  
  // Set the origin for a specific session topic
  setSessionOrigin(topic, origin) {
    if (topic && origin) {
      console.log(`Setting session origin for topic ${topic} to ${origin}`);
      this.sessionOrigins.set(topic, origin);
      
      // Persist to localStorage
      try {
        const savedOrigins = JSON.parse(localStorage.getItem('bitdog_connection_origins') || '{}');
        savedOrigins[topic] = origin;
        localStorage.setItem('bitdog_connection_origins', JSON.stringify(savedOrigins));
      } catch (error) {
        console.error('Error saving session origin to localStorage:', error);
      }
    }
  }
  
  // Get the origin for a specific session topic
  getSessionOrigin(topic) {
    // First check our in-memory map
    if (this.sessionOrigins.has(topic)) {
      return this.sessionOrigins.get(topic);
    }
    
    // Otherwise check localStorage
    try {
      const savedOrigins = JSON.parse(localStorage.getItem('bitdog_connection_origins') || '{}');
      if (savedOrigins[topic]) {
        // Update our in-memory map
        this.sessionOrigins.set(topic, savedOrigins[topic]);
        return savedOrigins[topic];
      }
    } catch (error) {
      console.error('Error loading session origin from localStorage:', error);
    }
    
    // Default to wallet if we can't determine
    return 'wallet';
  }
  
  // Enhanced permit transaction detection
  isPermitTransaction(data) {
    if (!data || typeof data !== 'string') return false;
    
    const lowerData = data.toLowerCase();
    
    return lowerData.includes('permit') || 
           lowerData.includes('0x2612065') ||   // Permit method signature
           lowerData.includes('0xd505accf') ||  // Permit signature for EIP-2612
           lowerData.includes('0x8fcbaf0c') ||  // Permit2 batch method id
           lowerData.includes('0x04b0792f') ||  // Another common permit variant
           lowerData.includes('0xf3995c67');    // Another permit signature
  }
  
  // Initialize the WalletKit with project ID
  async initialize(projectId, metadata, wallet, supportedChains) {
    if (this.initialized) {
      console.log('WalletKit already initialized');
      return true;
    }
    
    if (this.initializationInProgress) {
      console.log('WalletKit initialization already in progress');
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (this.initialized) {
            clearInterval(checkInterval);
            resolve(true);
          } else if (!this.initializationInProgress) {
            clearInterval(checkInterval);
            resolve(false);
          }
        }, 500);
      });
    }
    
    this.initializationInProgress = true;
  
    try {
      console.log('Initializing WalletKit with project ID:', projectId);
      
      if (!projectId) {
        throw new Error('Project ID is required for WalletKit initialization');
      }
      
      if (!wallet) {
        throw new Error('Wallet is required for WalletKit initialization');
      }
      
      if (!supportedChains || supportedChains.length === 0) {
        throw new Error('At least one supported chain is required for WalletKit initialization');
      }
      
      // Add the current domain to allowed origins in metadata
      if (metadata && !metadata.allowedOrigins) {
        const currentOrigin = window.location.origin;
        metadata.allowedOrigins = [currentOrigin];
        console.log('Added current origin to allowed origins:', currentOrigin);
      }
  
      // CRITICAL FIX: Use these Core options to bypass attestation errors
      const coreOptions = {
        projectId,
        logger: 'debug',
        relayUrl: 'wss://relay.walletconnect.com',
        relayFallbacks: [
          'wss://relay.walletconnect.org',
          'wss://relay.walletconnect.net'
        ],
        // These options fix the attestation and verification failures
        disableProviderPing: true,
        controller: true,
        disableVerifyClient: true, // Disable client verification
        skipVerifyPacket: true     // Skip packet verification
      };
      
      console.log('Creating Core with options:', JSON.stringify(coreOptions, null, 2));
      this.core = new Core(coreOptions);
  
      // Define wallet metadata with proper settings
      const walletMetadata = {
        name: metadata.name || 'BitDog Wallet',
        description: metadata.description || 'A secure multi-chain wallet',
        url: metadata.url || 'https://bitdogwallet.com',
        icons: metadata.icons || ['https://bitdogwallet.com/assets/wallet-logo.png'],
        redirect: {
          native: 'bitdogwallet://',
          universal: metadata.url || 'https://bitdogwallet.com'
        },
        // Add allowedOrigins from metadata or default to current origin
        allowedOrigins: metadata.allowedOrigins || [window.location.origin]
      };
  
      // Initialize WalletKit with retry mechanism
      let attempt = 0;
      const maxAttempts = 5;
      
      while (attempt < maxAttempts) {
        try {
          console.log(`WalletKit initialization attempt ${attempt + 1}`);
          
          // CRITICAL FIX: Add timeout and error handling for initialization
          const initPromise = WalletKit.init({
            core: this.core,
            metadata: walletMetadata
          });
          
          // Add a timeout to prevent hanging initialization
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('WalletKit initialization timed out')), 10000);
          });
          
          // Race the initialization against a timeout
          this.walletKit = await Promise.race([initPromise, timeoutPromise]);
          
          // Setup event listeners immediately
          this.setupEventListeners();
          
          this.initialized = true;
          this.initializationInProgress = false;
          console.log('WalletKit initialized successfully');
          
          // Store reference to current wallet and supported chains
          this.wallet = wallet;
          this.supportedChains = supportedChains;
          
          // Process any queued operations
          this.processOperationQueue();
          
          // Restore active sessions and re-establish event listeners
          setTimeout(async () => {
            try {
              await this.restoreSessions();
            } catch (restoreError) {
              console.error('Error in delayed session restore:', restoreError);
            }
          }, 500);
          
          return true;
        } catch (initError) {
          console.error(`WalletKit initialization attempt ${attempt + 1} failed:`, initError);
          attempt++;
          
          if (attempt < maxAttempts) {
            // Wait before retrying (exponential backoff with longer timeouts)
            const delay = 2000 * Math.pow(2, attempt);
            console.log(`Retrying in ${delay/1000} seconds...`);
            await new Promise(resolve => setTimeout(resolve, delay));
          } else {
            console.error('All WalletKit initialization attempts failed');
            this.initializationInProgress = false;
            return false;
          }
        }
      }
      
      this.initializationInProgress = false;
      return false;
    } catch (error) {
      console.error('Failed to initialize WalletKit:', error);
      this.initializationInProgress = false;
      return false;
    }
  }
  
  // Queue operations to run after initialization
  queueOperation(operation) {
    this.operationQueue.push(operation);
    console.log(`Operation queued. Queue length: ${this.operationQueue.length}`);
  }
  
  // Process queued operations after initialization
  processOperationQueue() {
    if (this.operationQueue.length === 0) {
      return;
    }
    
    console.log(`Processing ${this.operationQueue.length} queued operations`);
    
    // Take a copy of the queue and clear it
    const operations = [...this.operationQueue];
    this.operationQueue = [];
    
    // Execute each operation
    operations.forEach(operation => {
      try {
        operation();
      } catch (error) {
        console.error('Error executing queued operation:', error);
      }
    });
  }

  // Restore existing sessions
  async restoreSessions() {
    try {
      console.log('Restoring WalletConnect sessions...');
      
      if (!this.initialized || !this.walletKit) {
        console.warn('Cannot restore sessions: WalletKit not initialized');
        return false;
      }
      
      // Safely get active sessions
      let activeSessions = {};
      try {
        if (typeof this.walletKit.getActiveSessions === 'function') {
          activeSessions = this.walletKit.getActiveSessions() || {};
        } else {
          console.warn('getActiveSessions not available on walletKit');
          return false;
        }
      } catch (getSessionsError) {
        console.error('Error getting active sessions:', getSessionsError);
        return false;
      }
      
      console.log('Active sessions after init:', activeSessions);
      
      // Try to restore from localStorage as well if needed
      try {
        const storedSessions = JSON.parse(localStorage.getItem(SESSIONS_STORAGE_KEY) || '[]');
        
        // If no active sessions from walletKit but we have stored sessions, try to restore them
        if (Object.keys(activeSessions).length === 0 && storedSessions.length > 0) {
          console.log('No active sessions found in WalletKit but found stored sessions. Attempting recovery...');
          
          // Wait a moment before attempting recovery
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Try to reconnect
          await this.reconnect();
          
          // Get sessions again
          if (typeof this.walletKit.getActiveSessions === 'function') {
            activeSessions = this.walletKit.getActiveSessions() || {};
          }
        }
      } catch (storageError) {
        console.error('Error checking stored sessions:', storageError);
      }
      
      if (Object.keys(activeSessions).length === 0) {
        console.log('No active sessions found to restore');
        return false;
      }
      
      // Update our internal tracking
      this.activeSessions = Object.values(activeSessions);
      
      // Load saved connection origins
      try {
        const saved = localStorage.getItem('bitdog_connection_origins');
        if (saved) {
          const connectionOrigins = JSON.parse(saved);
          
          // Populate our in-memory map of session origins
          Object.entries(connectionOrigins).forEach(([topic, origin]) => {
            this.sessionOrigins.set(topic, origin);
          });
        }
      } catch (error) {
        console.error('Failed to load connection origins:', error);
      }
      
      // For each active session, ensure we have a handler location
      const restoredSessions = [];
      this.activeSessions.forEach(session => {
        // Make sure we have a valid session with a topic
        if (session && session.topic) {
          // Get the origin from our map, or default to 'wallet'
          const origin = this.getSessionOrigin(session.topic);
          
          restoredSessions.push({
            topic: session.topic,
            peer: session.peer,
            namespaces: session.namespaces,
            wcHandlerLocation: origin
          });
        }
      });
      
      if (restoredSessions.length > 0) {
        console.log(`Restored ${restoredSessions.length} WalletConnect sessions with handler locations`);
        
        // Save sessions to storage for reference
        localStorage.setItem(SESSIONS_STORAGE_KEY, JSON.stringify(restoredSessions));
        return true;
      } else {
        console.log('No valid sessions to restore');
        return false;
      }
    } catch (error) {
      console.error('Error restoring sessions:', error);
      return false;
    }
  }

  // Setup event listeners for WalletKit events
  setupEventListeners() {
    if (!this.walletKit) {
      console.error('Cannot setup event listeners: WalletKit not initialized');
      return;
    }
  
    // Listen for session proposals
    this.walletKit.on('session_proposal', async (proposal) => {
      console.log('Received session proposal:', proposal);
      this.pendingProposal = proposal;
      
      if (this.onSessionProposalCallback) {
        // Pass the current handler location
        this.onSessionProposalCallback(proposal, this.wcHandlerLocation);
      } else {
        console.warn('No session proposal callback registered');
      }
    });
  
    // Listen for session requests with enhanced handling
    this.walletKit.on('session_request', async (requestEvent) => {
      console.log('Received session request:', requestEvent);
      
      try {
        // Extract method and params using a resilient approach
        let method, params;
        
        if (requestEvent && requestEvent.params && requestEvent.params.request) {
          // Standard format
          method = requestEvent.params.request.method;
          params = requestEvent.params.request.params;
        } else if (requestEvent.method) {
          // Direct format
          method = requestEvent.method;
          params = requestEvent.params;
        } else {
          console.error('Cannot extract method from request:', requestEvent);
          this.pendingRequests.push(requestEvent);
          
          if (this.onSessionRequestCallback) {
            this.onSessionRequestCallback(requestEvent, this.wcHandlerLocation);
          }
          return;
        }
        
        console.log('Request method:', method);
        console.log('Request params:', JSON.stringify(params, null, 2));
        
        // Track signature request type and context
        if (method && method.startsWith('eth_signTypedData')) {
          this.lastSignatureRequestMethod = method;
          
          // Detect Uniswap Permit signatures
          try {
            let typedDataParam;
            if (Array.isArray(params) && params.length > 1) {
              typedDataParam = params[1];
            } else if (params && params.data) {
              typedDataParam = params.data;
            }
            
            if (typedDataParam) {
              let typedData;
              
              if (typeof typedDataParam === 'string') {
                try {
                  typedData = JSON.parse(typedDataParam);
                } catch (e) {
                  // Not JSON, ignore
                  console.log('Could not parse typed data as JSON');
                }
              } else {
                typedData = typedDataParam;
              }
              
              // Enhanced Uniswap Permit detection
              if (typedData && typedData.domain) {
                const domainName = typedData.domain.name;
                const hasPermitType = typedData.types && 
                  (typedData.types.PermitSingle || 
                   typedData.types.Permit || 
                   typedData.types.PermitBatch);
                
                const isUniswap = 
                  (domainName === 'Permit2' || 
                   domainName === 'Uniswap V3 Router' ||
                   domainName === 'Uniswap Permit2' ||
                   (domainName && domainName.includes('Uniswap')));
                
                if ((isUniswap || hasPermitType)) {
                  console.log('DETECTED UNISWAP PERMIT SIGNATURE');
                  console.log('Domain name:', domainName);
                  
                  if (hasPermitType) {
                    console.log('Permit type detected:', 
                      typedData.types.PermitSingle ? 'PermitSingle' :
                      typedData.types.Permit ? 'Permit' : 'PermitBatch');
                  }
                  
                  this.lastPermitContext = 'uniswap';
                } else {
                  this.lastPermitContext = null;
                }
              } else {
                this.lastPermitContext = null;
              }
            }
          } catch (detectionError) {
            console.log('Error in permit detection:', detectionError);
            this.lastPermitContext = null;
          }
        } else {
          this.lastSignatureRequestMethod = method;
          this.lastPermitContext = null;
        }
        
        // Get the session origin for this topic
        const sessionOrigin = this.getSessionOrigin(requestEvent.topic);
        console.log(`Session origin for topic ${requestEvent.topic}: ${sessionOrigin}`);
        
        // Check if this is a permit transaction
        let isPermit = false;
        let txData = null;
        
        if (method === 'eth_sendTransaction') {
          // Safely extract transaction data
          try {
            if (Array.isArray(params) && params.length > 0 && params[0]) {
              txData = params[0].data;
            }
            // Check if this is a permit transaction
            if (txData) {
              isPermit = this.isPermitTransaction(txData);
              
              if (isPermit) {
                console.log('PERMIT TRANSACTION DETECTED');
              }
            }
          } catch (dataError) {
            console.error('Error processing transaction data:', dataError);
          }
        }
        
        // Group methods by type
        const transactionMethods = [
          'eth_sendTransaction', 
          'eth_signTransaction',
          'wallet_sendCalls'
        ];
        const signatureMethods = [
          'eth_sign', 
          'personal_sign', 
          'eth_signTypedData', 
          'eth_signTypedData_v4',
          'eth_signTypedData_v3'
        ];
        const chainMethods = [
          'wallet_switchEthereumChain', 
          'wallet_addEthereumChain'
        ];
        
        let requestType = 'other';
        
        if (transactionMethods.includes(method)) {
          requestType = 'transaction';
        } else if (signatureMethods.includes(method) || method.startsWith('eth_signTypedData')) {
          requestType = 'signature';
          console.log('SIGNATURE REQUEST DETECTED');
        } else if (chainMethods.includes(method)) {
          requestType = 'chainSwitch';
        } else if (method === 'wallet_requestPermissions' || method === 'wallet_getPermissions') {
          requestType = 'permissions';
        }
        
        // Determine handler location
        // For signatures and permits, always use wallet component for better security isolation
        const handlerLocation = isPermit || this.lastPermitContext === 'uniswap' || 
                              requestType === 'signature' ? 'wallet' : sessionOrigin;
        
        // Create enhanced request object
        const enhancedRequest = {
          id: requestEvent.id,
          topic: requestEvent.topic,
          type: requestType,
          method,
          params,
          source: 'walletconnect',
          url: this.getSessionUrl(requestEvent.topic),
          timestamp: Date.now(),
          wcHandlerLocation: handlerLocation,
          isPermit,
          isPostPermitSwap: this.lastPermitContext === 'uniswap' && !isPermit,
          originalEvent: requestEvent
        };
        
        // Add to pending requests
        this.pendingRequests.push(enhancedRequest);
        
        if (this.onSessionRequestCallback) {
          // Pass the enhanced request
          this.onSessionRequestCallback(enhancedRequest, handlerLocation);
        } else {
          console.warn('No session request callback registered');
        }
      } catch (error) {
        console.error('Error processing session request:', error);
        // Still try to process
        this.pendingRequests.push(requestEvent);
        
        if (this.onSessionRequestCallback) {
          this.onSessionRequestCallback(requestEvent, this.wcHandlerLocation);
        }
      }
    });

    // Listen for auth requests
    this.walletKit.on('session_authenticate', async (authEvent) => {
      console.log('Received auth request:', authEvent);
      
      const handlerLocation = this.wcHandlerLocation || 'wallet';
      
      if (this.onAuthRequestCallback) {
        this.onAuthRequestCallback(authEvent, handlerLocation);
      } else {
        console.warn('No auth request callback registered');
      }
    });

    // Listen for session deletion
    this.walletKit.on('session_delete', ({ id, topic }) => {
      console.log(`Session deleted: ${id}`);
      this.activeSessions = this.activeSessions.filter(session => session.topic !== topic);
      
      // Remove from session origins tracking
      this.sessionOrigins.delete(topic);
      
      // Update localStorage
      try {
        const savedOrigins = JSON.parse(localStorage.getItem('bitdog_connection_origins') || '{}');
        delete savedOrigins[topic];
        localStorage.setItem('bitdog_connection_origins', JSON.stringify(savedOrigins));
      } catch (error) {
        console.error('Error updating localStorage after session deletion:', error);
      }
      
      // Update the stored sessions
      try {
        const storedSessions = JSON.parse(localStorage.getItem(SESSIONS_STORAGE_KEY) || '[]');
        const updatedSessions = storedSessions.filter(session => session.topic !== topic);
        localStorage.setItem(SESSIONS_STORAGE_KEY, JSON.stringify(updatedSessions));
      } catch (error) {
        console.error('Error updating stored sessions after deletion:', error);
      }
    });
    
    // Listen for errors
    this.walletKit.on('error', (error) => {
      console.error('WalletKit error:', error);
      
      // Check if it's a connection error and handle reconnection
      if (error && (
        error.message && (
          error.message.includes('connection') || 
          error.message.includes('websocket') || 
          error.message.includes('network')
        )
      )) {
        this.handleConnectionError();
      }
    });
    
    // Add listener for proposal expiration
    this.walletKit.on('proposal_expire', (event) => {
      console.log(`Proposal expired: ${event.id}`);
      // Clear if it's our current pending proposal
      if (this.pendingProposal && this.pendingProposal.id === event.id) {
        this.pendingProposal = null;
      }
    });
    
    // Add listener for request expiration
    this.walletKit.on('session_request_expire', (event) => {
      console.log(`Request expired: ${event.id}`);
      this.pendingRequests = this.pendingRequests.filter(req => req.id !== event.id);
    });
    
    // Add connection status monitoring
    this.walletKit.on('connect', () => {
      console.log('WalletConnect relay connected');
      this.connectionRetries = 0; // Reset retry counter on successful connection
    });
    
    this.walletKit.on('disconnect', () => {
      console.log('WalletConnect relay disconnected');
      this.handleConnectionError();
    });
  }

  // Handle connection errors with retry mechanism
  handleConnectionError() {
    if (this.connectionRetries < this.maxRetries) {
      this.connectionRetries++;
      
      // Clear any existing reconnect timeout
      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout);
      }
      
      const delay = 1000 * Math.pow(2, this.connectionRetries); // Exponential backoff
      console.log(`WalletConnect will attempt to reconnect in ${delay/1000} seconds (attempt ${this.connectionRetries})`);
      
      this.reconnectTimeout = setTimeout(() => {
        console.log(`Attempting WalletConnect reconnection (attempt ${this.connectionRetries})`);
        
        // Try to reinitialize core with different relay URL
        if (this.core) {
          try {
            // Switch relay URL for each retry attempt
            const relayUrls = [
              'wss://relay.walletconnect.com',
              'wss://relay.walletconnect.org',
              'wss://relay.walletconnect.net'
            ];
            
            const relayIndex = (this.connectionRetries - 1) % relayUrls.length;
            const relayUrl = relayUrls[relayIndex];
            
            console.log(`Trying alternative relay URL: ${relayUrl}`);
            
            // Attempt to reconnect
            if (this.core.relayer && typeof this.core.relayer.transportToggle === 'function') {
              this.core.relayer.transportToggle();
            }
          } catch (error) {
            console.error('Failed to toggle WalletConnect transport:', error);
          }
        }
      }, delay);
    } else {
      console.error(`WalletConnect reconnection failed after ${this.maxRetries} attempts`);
    }
  }

  // Connect to dApp using WalletConnect URI
  async pair(uri) {
    if (!this.initialized || !this.walletKit) {
      if (!this.initialized) {
        console.error('WalletKit not initialized for pairing');
        return false;
      }
      
      // Queue the pairing operation for when WalletKit is initialized
      return new Promise((resolve, reject) => {
        this.queueOperation(async () => {
          try {
            const result = await this.pair(uri);
            resolve(result);
          } catch (error) {
            reject(error);
          }
        });
      });
    }

    try {
      console.log('Pairing with URI:', uri);
      
      // Validate URI format
      if (!uri || !uri.startsWith('wc:')) {
        throw new Error('Invalid WalletConnect URI format');
      }
      
      // Reset retry counter before new pairing attempt
      this.connectionRetries = 0;
      
      // Attempt to pair with the URI
      await this.walletKit.pair({ uri });
      console.log('Pairing request sent successfully');
      return true;
    } catch (error) {
      console.error('Failed to pair with URI:', error);
      
      // If failed due to WalletKit not being ready, queue for retry
      if (error.message && error.message.includes('not initialized')) {
        return new Promise((resolve, reject) => {
          this.queueOperation(async () => {
            try {
              const result = await this.pair(uri);
              resolve(result);
            } catch (retryError) {
              reject(retryError);
            }
          });
        });
      }
      
      throw error;
    }
  }

  // Respond to a session request with enhanced handling for signatures
  async respondSessionRequest({ topic, response }) {
    if (!this.initialized || !this.walletKit) {
      throw new Error('WalletKit not initialized');
    }
  
    try {
      console.log(`Responding to session request on topic ${topic}:`, 
        typeof response === 'object' ? JSON.stringify(response, null, 2) : response);
      
      // Format response properly with enhanced error handling
      let formattedResponse;
      
      // Check if response is already in the expected format
      if (response.jsonrpc && (response.result !== undefined || response.error !== undefined)) {
        formattedResponse = response;
      } else {
        formattedResponse = {
          id: response.id,
          jsonrpc: '2.0',
          result: response.result === undefined ? null : response.result
        };
        
        if (response.error) {
          formattedResponse.error = response.error;
          delete formattedResponse.result;
        }
      }
      
      // Ensure id is a number if possible
      if (formattedResponse.id && typeof formattedResponse.id === 'string') {
        const numId = parseInt(formattedResponse.id);
        if (!isNaN(numId)) {
          formattedResponse.id = numId;
          console.log('Converted string id to number:', numId);
        }
      }
      
      // CRITICAL FIX: Special handling for signature responses
      const isSignatureResponse = 
        typeof formattedResponse.result === 'string' && 
        formattedResponse.result.match(/^(0x)?[0-9a-fA-F]{130,}$/);
      
      // Ensure signature results always have 0x prefix
      if (isSignatureResponse && !formattedResponse.result.startsWith('0x')) {
        console.log('Adding 0x prefix to signature result');
        formattedResponse.result = '0x' + formattedResponse.result;
      }
      
      // Clean up any whitespace in the signature
      if (typeof formattedResponse.result === 'string') {
        formattedResponse.result = formattedResponse.result.trim();
      }
      
      console.log('Sending formatted response:', JSON.stringify(formattedResponse, null, 2));
      
      // Add delay for signature responses to ensure proper processing
      if (isSignatureResponse) {
        console.log('Adding delay for signature response');
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      // CRITICAL FIX: Retry mechanism for sending responses
      let attempt = 0;
      const maxAttempts = 3;
      let lastError;
      
      while (attempt < maxAttempts) {
        try {
          console.log(`Attempt ${attempt + 1} to send response`);
          // Use a timeout to prevent hanging operations
          const sendPromise = this.walletKit.respondSessionRequest({
            topic,
            response: formattedResponse
          });
          
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Send response timed out')), 5000);
          });
          
          await Promise.race([sendPromise, timeoutPromise]);
          
          console.log('Session request response sent successfully');
          
          // Handle special case for EIP-712 typed data signatures
          if (isSignatureResponse && 
              this.lastSignatureRequestMethod?.startsWith('eth_signTypedData')) {
            console.log('Sent EIP-712 signature successfully');
            
            // If this was a Uniswap PermitSingle signature, send a notification
            if (this.lastPermitContext === 'uniswap') {
              console.log('Sending supplementary notification for Uniswap');
              try {
                // This is optional and won't affect overall success
                await this.notifyPermitCompletion(topic, formattedResponse.id, formattedResponse.result);
              } catch (notifyError) {
                console.log('Supplementary notification failed (this is ok):', notifyError);
              }
            }
          }
          
          return true;
        } catch (error) {
          console.error(`Failed to respond on attempt ${attempt + 1}:`, error);
          lastError = error;
          attempt++;
          
          if (attempt < maxAttempts) {
            // Wait before retrying
            const delay = 1000 * Math.pow(2, attempt - 1);
            console.log(`Retrying in ${delay/1000} seconds...`);
            await new Promise(resolve => setTimeout(resolve, delay));
          } else {
            console.error('All response attempts failed');
            throw lastError;
          }
        }
      }
      
      throw new Error('Failed to send response after maximum attempts');
    } catch (error) {
      console.error('Failed to respond to session request:', error);
      throw error;
    }
  }

  // Helper method for Uniswap notifications
  async notifyPermitCompletion(topic, requestId, signature) {
    try {
      if (!this.walletKit || !this.initialized) return;
      
      // Try to send an event to the dApp
      await this.walletKit.emitSessionEvent({
        topic,
        event: {
          name: 'permitSignatureCompleted',
          data: {
            id: requestId,
            success: true,
            signature: signature
          }
        }
      });
      
      console.log('Sent permit completion notification');
    } catch (error) {
      console.log('Failed to notify permit completion:', error);
    }
  }

  // Approve a session proposal
  async approveSession(proposalId, wallet, chainId) {
    if (!this.initialized || !this.walletKit) {
      throw new Error('WalletKit not initialized');
    }
  
    if (!this.pendingProposal) {
      throw new Error('No pending session proposal');
    }
  
    try {
      const { id, params } = this.pendingProposal;
      const { requiredNamespaces, optionalNamespaces } = params;
      
      console.log('Proposal namespaces:', { required: requiredNamespaces, optional: optionalNamespaces });
      
      // Format wallet address for CAIP-10
      const formattedAddress = wallet.address.toLowerCase();
      
      // Use the utility function to build approved namespaces
      const approvedNamespaces = buildApprovedNamespaces({
        proposal: params,
        supportedNamespaces: {
          eip155: {
            chains: [`eip155:${chainId}`, `eip155:1`], // Support Ethereum mainnet and current chain
            methods: [
              "eth_accounts",
              "eth_requestAccounts",
              "eth_sendRawTransaction",
              "eth_sign",
              "eth_signTransaction",
              "eth_signTypedData",
              "eth_signTypedData_v3",
              "eth_signTypedData_v4",
              "eth_sendTransaction",
              "personal_sign",
              "wallet_switchEthereumChain",
              "wallet_addEthereumChain",
              "wallet_getPermissions",
              "wallet_requestPermissions",
              "wallet_registerOnboarding",
              "wallet_watchAsset",
              "wallet_scanQRCode",
              "wallet_sendCalls",
              "wallet_getCallsStatus",
              "wallet_showCallsStatus",
              "wallet_getCapabilities",
            ],
            events: [
              "chainChanged",
              "accountsChanged",
              "message",
              "disconnect",
              "connect",
            ],
            accounts: [
              `eip155:${chainId}:${formattedAddress}`,
              `eip155:1:${formattedAddress}`
            ]
          }
        }
      });
      
      console.log('Approving session with namespaces:', approvedNamespaces);
  
      // Approve the session
      const session = await this.walletKit.approveSession({
        id: proposalId || id,
        namespaces: approvedNamespaces
      });
  
      console.log('Session approved:', session);
      this.activeSessions.push(session);
      this.pendingProposal = null;
      
      // Store the handler location for this session
      this.setSessionOrigin(session.topic, this.wcHandlerLocation || 'wallet');
      
      // Save session to storage
      try {
        const storedSessions = JSON.parse(localStorage.getItem(SESSIONS_STORAGE_KEY) || '[]');
        storedSessions.push({
          topic: session.topic,
          peer: session.peer,
          namespaces: session.namespaces,
          wcHandlerLocation: this.wcHandlerLocation || 'wallet'
        });
        localStorage.setItem(SESSIONS_STORAGE_KEY, JSON.stringify(storedSessions));
      } catch (storageError) {
        console.error('Error saving session to storage:', storageError);
      }
      
      return session;
    } catch (error) {
      console.error('Failed to approve session:', error);
      throw error;
    }
  }

  // Reject a session proposal
  async rejectSession(proposalId, reason = 'USER_REJECTED') {
    if (!this.initialized || !this.walletKit) {
      throw new Error('WalletKit not initialized');
    }

    try {
      const id = proposalId || (this.pendingProposal ? this.pendingProposal.id : null);
      
      if (!id) {
        throw new Error('No session proposal ID provided');
      }
      
      await this.walletKit.rejectSession({
        id,
        reason: getSdkError(reason)
      });
      
      this.pendingProposal = null;
      console.log('Session rejected');
      return true;
    } catch (error) {
      console.error('Failed to reject session:', error);
      throw error;
    }
  }

  // Approve a request (transaction, signature, etc.)
  async approveRequest(requestId, result) {
    if (!this.initialized || !this.walletKit) {
      throw new Error('WalletKit not initialized');
    }

    try {
      const requestIndex = this.pendingRequests.findIndex(req => req.id === requestId);
      if (requestIndex === -1) {
        throw new Error('Request not found');
      }

      // Get the topic for this request
      const topic = this.pendingRequests[requestIndex].topic;
      
      if (!topic) {
        throw new Error('Request has no topic');
      }
      
      await this.walletKit.respondSessionRequest({
        topic: topic,
        response: {
          id: requestId,
          jsonrpc: '2.0',
          result
        }
      });

      // Remove the request from pending
      this.pendingRequests.splice(requestIndex, 1);
      console.log('Request approved');
      return true;
    } catch (error) {
      console.error('Failed to approve request:', error);
      throw error;
    }
  }

  // Reject a request
  async rejectRequest(requestId, reason = 'USER_REJECTED') {
    if (!this.initialized || !this.walletKit) {
      throw new Error('WalletKit not initialized');
    }

    try {
      const requestIndex = this.pendingRequests.findIndex(req => req.id === requestId);
      if (requestIndex === -1) {
        throw new Error('Request not found');
      }

      // Get the topic for this request
      const topic = this.pendingRequests[requestIndex].topic;
      
      if (!topic) {
        throw new Error('Request has no topic');
      }

      await this.walletKit.respondSessionRequest({
        topic: topic,
        response: {
          id: requestId,
          jsonrpc: '2.0',
          error: {
            code: 4001,
            message: reason
          }
        }
      });

      // Remove the request from pending
      this.pendingRequests.splice(requestIndex, 1);
      console.log('Request rejected');
      return true;
    } catch (error) {
      console.error('Failed to reject request:', error);
      throw error;
    }
  }

  // Handle authentication requests
  async approveSessionAuthenticate(authParams) {
    if (!this.initialized || !this.walletKit) {
      throw new Error('WalletKit not initialized');
    }
    
    try {
      await this.walletKit.approveSessionAuthenticate(authParams);
      return true;
    } catch (error) {
      console.error('Failed to approve authentication:', error);
      throw error;
    }
  }
  
  // Reject authentication request
  async rejectSessionAuthenticate(authParams) {
    if (!this.initialized || !this.walletKit) {
      throw new Error('WalletKit not initialized');
    }
    
    try {
      await this.walletKit.rejectSessionAuthenticate(authParams);
      return true;
    } catch (error) {
      console.error('Failed to reject authentication:', error);
      throw error;
    }
  }
  
  // Format auth message
  formatAuthMessage(params) {
    if (!this.initialized || !this.walletKit) {
      throw new Error('WalletKit not initialized');
    }
    
    return this.walletKit.formatAuthMessage(params);
  }

  // Get all active sessions with improved error handling
  getActiveSessions() {
    if (!this.initialized || !this.walletKit) {
      console.warn('WalletKit not initialized, returning empty sessions object');
      return {};
    }
    
    try {
      if (typeof this.walletKit.getActiveSessions !== 'function') {
        console.error('getActiveSessions is not a function on walletKit');
        return {};
      }
      
      const sessions = this.walletKit.getActiveSessions();
      return sessions || {};
    } catch (error) {
      console.error('Failed to get active sessions:', error);
      // Return empty object instead of throwing error to prevent UI crashes
      return {};
    }
  }

  // Disconnect a session with better error handling
  async disconnectSession(topic) {
    console.log(`Attempting to disconnect session with topic: ${topic}`);
    
    if (!this.initialized || !this.walletKit) {
      console.error('WalletKit not initialized for disconnection');
      
      // Still remove from our local storage to clear UI
      this.removeSessionFromStorage(topic);
      return false;
    }
  
    try {
      // Safety check for valid topic
      if (!topic) {
        console.error('Invalid topic provided for disconnection');
        return false;
      }
      
      // Even if WalletKit call fails, we'll still clean up local storage
      try {
        await this.walletKit.disconnectSession({
          topic,
          reason: getSdkError('USER_DISCONNECTED')
        });
        console.log(`WalletKit disconnection successful for topic: ${topic}`);
      } catch (disconnectError) {
        console.error(`Error in WalletKit disconnection for topic ${topic}:`, disconnectError);
        // Continue with cleanup even if WalletKit call fails
      }
      
      // Clean up our internal state regardless of WalletKit result
      this.removeSessionFromStorage(topic);
      console.log('Session disconnected from local storage');
      
      // Important: Try to remove from wallet core directly to ensure complete cleanup
      try {
        if (this.core && this.core.pairing && typeof this.core.pairing.disconnect === 'function') {
          await this.core.pairing.disconnect({ topic });
        }
      } catch (coreError) {
        console.warn('Error disconnecting from core pairing:', coreError);
      }
      
      return true;
    } catch (error) {
      console.error('Failed to disconnect session:', error);
      
      // Still try to clean up local storage
      this.removeSessionFromStorage(topic);
      return false;
    }
  }
  
  // Helper to remove a session from local storage
  removeSessionFromStorage(topic) {
    // Remove from active sessions
    this.activeSessions = this.activeSessions.filter(session => session.topic !== topic);
    
    // Remove from session origins tracking
    this.sessionOrigins.delete(topic);
    
    // Update localStorage for connection origins
    try {
      const savedOrigins = JSON.parse(localStorage.getItem('bitdog_connection_origins') || '{}');
      delete savedOrigins[topic];
      localStorage.setItem('bitdog_connection_origins', JSON.stringify(savedOrigins));
    } catch (error) {
      console.error('Error updating localStorage after disconnection:', error);
    }
    
    // Update stored sessions
    try {
      const storedSessions = JSON.parse(localStorage.getItem(SESSIONS_STORAGE_KEY) || '[]');
      const updatedSessions = storedSessions.filter(session => session.topic !== topic);
      localStorage.setItem(SESSIONS_STORAGE_KEY, JSON.stringify(updatedSessions));
    } catch (storageError) {
      console.error('Error updating stored sessions after disconnect:', storageError);
    }
  }
  
  // Force disconnect all sessions - helpful for clearing stuck connections
  async disconnectAllSessions() {
    console.log('Attempting to disconnect all sessions');
    
    // Get all session topics
    const topics = this.activeSessions.map(session => session.topic);
    
    // Also get any from localStorage that might not be in memory
    try {
      const storedSessions = JSON.parse(localStorage.getItem(SESSIONS_STORAGE_KEY) || '[]');
      storedSessions.forEach(session => {
        if (session.topic && !topics.includes(session.topic)) {
          topics.push(session.topic);
        }
      });
    } catch (error) {
      console.error('Error reading stored sessions:', error);
    }
    
    console.log(`Found ${topics.length} sessions to disconnect`);
    
    // Disconnect each session
    const results = await Promise.all(
      topics.map(async (topic) => {
        try {
          await this.disconnectSession(topic);
          return { topic, success: true };
        } catch (error) {
          console.error(`Error disconnecting session ${topic}:`, error);
          return { topic, success: false };
        }
      })
    );
    
    // Clear all storage regardless of individual results
    this.activeSessions = [];
    this.sessionOrigins.clear();
    localStorage.setItem(SESSIONS_STORAGE_KEY, '[]');
    localStorage.setItem('bitdog_connection_origins', '{}');
    
    console.log('All sessions disconnected and storage cleared');
    
    return results;
  }

  // Set callback for session proposals
  onSessionProposal(callback) {
    this.onSessionProposalCallback = callback;
  }

  // Set callback for session requests
  onSessionRequest(callback) {
    this.onSessionRequestCallback = callback;
  }

  // Set callback for auth requests
  onAuthRequest(callback) {
    this.onAuthRequestCallback = callback;
  }
  
  // Utility method to get session URL from topic
  getSessionUrl(topic) {
    try {
      // Try getting sessions from activeSessions
      const connection = this.activeSessions.find(conn => conn.topic === topic);
      if (connection && connection.peer && connection.peer.metadata) {
        return connection.peer.metadata.url || 'Unknown';
      }
      
      // Try getting from walletKit.getActiveSessions() as fallback
      try {
        const activeSessions = this.getActiveSessions();
        const session = Object.values(activeSessions).find(s => s.topic === topic);
        
        if (session && session.peer && session.peer.metadata) {
          return session.peer.metadata.url || 'Unknown';
        }
      } catch (error) {
        console.warn('Error getting session URL from walletKit:', error);
      }
      
      return 'Unknown';
    } catch (error) {
      console.error('Error getting session URL:', error);
      return 'Unknown';
    }
  }
  
  // Manual reconnection method that apps can call when connection issues are detected
  async reconnect() {
    if (!this.initialized || !this.core || !this.core.relayer) {
      return false;
    }
    
    try {
      console.log('Attempting manual reconnection to WalletConnect relay');
      
      if (this.core.relayer && typeof this.core.relayer.transportToggle === 'function') {
        this.core.relayer.transportToggle();
        console.log('Toggled WalletConnect transport for reconnection');
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error in manual reconnection:', error);
      return false;
    }
  }
  
  // Helper method to check connection status
  isConnected() {
    if (!this.initialized || !this.core || !this.core.relayer) {
      return false;
    }
    
    try {
      return this.core.relayer.connected;
    } catch (error) {
      console.error('Error checking connection status:', error);
      return false;
    }
  }
  
  // Reset the entire WalletConnect state (useful for troubleshooting)
  async reset() {
    console.log('Resetting WalletConnect service');
    
    // Disconnect all sessions
    await this.disconnectAllSessions();
    
    // Clear all state
    this.initialized = false;
    this.pendingProposal = null;
    this.activeSessions = [];
    this.pendingRequests = [];
    this.sessionOrigins.clear();
    this.connectionRetries = 0;
    this.lastSignatureRequestMethod = null;
    this.lastPermitContext = null;
    
    // Clear localStorage
    localStorage.removeItem(SESSIONS_STORAGE_KEY);
    localStorage.removeItem('bitdog_connection_origins');
    
    // Clear the core and walletKit
    this.core = null;
    this.walletKit = null;
    
    console.log('WalletConnect service reset complete');
    return true;
  }
}

// Export singleton instance
const walletConnectService = new WalletConnectService();
export default walletConnectService;